import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import type { Order, Shipment } from "@/api.types.v2";
import { logger } from "@/services/logger";
import type { OrderDraft } from "./types";

const DRAFT_KEY = "ezx.create-order-draft.v1";
const SAVE_DEBOUNCE_MS = 600;

interface CreateOrderContextValue {
  data: OrderDraft;
  update: (patch: Partial<OrderDraft>) => void;
  reset: () => void;
  persistDraft: () => boolean;
  /** Clear the persisted draft + in-memory data without touching the
   * created-order state. Called after a successful order placement so
   * the confirmation screen can still read createdOrder. */
  discardDraft: () => void;
  createdOrder: Order | null;
  setCreatedOrder: (order: Order | null) => void;
  createdShipments: Shipment[];
  setCreatedShipments: (shipments: Shipment[]) => void;
  /** ms-since-epoch of the last successful sessionStorage write, or null. */
  lastSavedAt: number | null;
}

const CreateOrderContext = createContext<CreateOrderContextValue | null>(null);

function loadDraft(): OrderDraft {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as OrderDraft;
  } catch (e) {
    logger.error("Failed to read order draft from sessionStorage", e);
    return {};
  }
}

function clearDraft() {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch (e) {
    logger.error("Failed to clear order draft", e);
  }
}

export function CreateOrderProvider({ children }: { children: ReactNode }) {
  // Lazy init pulls from sessionStorage on first render.
  const [data, setData] = useState<OrderDraft>(() => loadDraft());
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [createdShipments, setCreatedShipments] = useState<Shipment[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(() =>
    Object.keys(loadDraft()).length > 0 ? Date.now() : null
  );

  const persistDraft = useCallback(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(data));
      setLastSavedAt(Date.now());
      return true;
    } catch (error) {
      logger.error("Failed to persist order draft", error);
      return false;
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(data).length === 0) return;
    const timer = window.setTimeout(persistDraft, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [data, persistDraft]);

  const update = (patch: Partial<OrderDraft>) =>
    setData((prev) => ({ ...prev, ...patch }));

  const discardDraft = () => {
    setData({});
    setLastSavedAt(null);
    clearDraft();
  };

  const reset = () => {
    discardDraft();
    setCreatedOrder(null);
    setCreatedShipments([]);
  };

  return (
    <CreateOrderContext.Provider
      value={{
        data,
        update,
        reset,
        persistDraft,
        discardDraft,
        createdOrder,
        setCreatedOrder,
        createdShipments,
        setCreatedShipments,
        lastSavedAt,
      }}
    >
      {children}
    </CreateOrderContext.Provider>
  );
}

export function useCreateOrder() {
  const ctx = useContext(CreateOrderContext);
  if (!ctx)
    throw new Error("useCreateOrder must be used inside CreateOrderProvider");
  return ctx;
}
